import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/styled-system'
import { siteNavigationColorKeys } from '~/pages/Home/SiteNavigation/componentStyles/_colors'
import { asChakraVariable } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

export enum SiteNavigationTabSizes {
  Normal = 'normal',
  Collapsed = 'collapsed',
  CollapsedBadged = 'collapsedBadged',
}

const SiteNavigationTabParts = [
  'container',
  'selectionContainer',
  'contentWrapper',
  'iconWrapper',
  'icon',
  'titleText',
  'notificationBadge',
  'statusBadge',
] as const

const { Background, BoxShadow, Icon, FontColor } =
  siteNavigationColorKeys.SiteNavigationTab
const helpers = createMultiStyleConfigHelpers(SiteNavigationTabParts)

export const SiteNavigationTabStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      gap: '0.125rem',
    },
    selectionContainer: {
      display: 'flex',
      height: '3rem',
      py: '0.625rem',
      userSelect: 'none',
      cursor: 'pointer',
      borderRadius: '0.5rem',
      position: 'relative',
      gap: '0.125rem',
      _hover: {
        bg: Background.Hover,
      },
      _active: {
        bg: Background.Active,
      },
      _selected: {
        bg: Background.Selected,
        boxShadow: `0px 1px 0px 0px ${asChakraVariable(BoxShadow.Selected)}`,
      },
    },
    contentWrapper: {
      alignItems: 'center',
      flex: 1,
    },
    iconWrapper: {
      flex: 1,
      gap: '0.5rem',
      alignItems: 'center',
    },
    icon: {
      boxSize: '1.5rem',
      color: Icon.Default,
      _selected: {
        color: Icon.Selected,
      },
    },
    titleText: {
      textStyle: textStyleKeys.Body2_Semibold,
      color: FontColor.Default,
      _selected: {
        color: FontColor.Selected,
      },
    },
    statusBadge: {
      zIndex: 1,
    },
  },
  sizes: {
    [SiteNavigationTabSizes.Normal]: {
      container: {
        flexDirection: 'column',
        alignSelf: 'stretch',
      },
      selectionContainer: {
        alignItems: 'center',
        alignSelf: 'stretch',
        pl: '0.75rem',
        pr: '0.5rem',
      },
    },
    [SiteNavigationTabSizes.Collapsed]: {
      selectionContainer: {
        width: '3rem',
        height: '3rem',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
      },
      notificationBadge: {
        position: 'absolute',
        top: '-0.125rem',
        right: '-0.125rem',
      },
    },
    [SiteNavigationTabSizes.CollapsedBadged]: {
      container: {
        width: '3rem',
        height: '3.5625rem',
      },
      selectionContainer: {
        flexDirection: 'column',
        px: '0.75rem',
        py: '0.625rem',
        width: '3rem',
        height: '3rem',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      },
    },
  },
  defaultProps: {
    size: SiteNavigationTabSizes.Normal,
  },
})

export const useSiteNavigationTabStyle = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof SiteNavigationTabParts>(
    getName({ SiteNavigationTabStyle }),
    props,
  )
}
